import {
  ClientLoginRequest,
  ClientLoginResponse,
  ClientRegisterRequest,
  ClientRegisterResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetConfirm,
  EmailVerificationRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ClientUser,
} from '@digitaltattoo/types';

// Always use relative URLs by default so requests go to the same origin as the frontend.
// This works on any device (mobile, desktop) via Vite proxy in dev, or a reverse
// proxy / same-origin serving in production.sync resendVerificationEmail(
// Set VITE_API_URL only when the API lives on a completely different domain.
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

class ClientAuthApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/api/client-auth`;
  }

  /**
   * Register a new client account
   */
  async register(data: ClientRegisterRequest): Promise<ClientRegisterResponse> {
    const response = await fetch(`${this.baseUrl}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Registration failed');
    }

    return result;
  }
async resendVerificationEmail(
  email: string
): Promise<{
  success: boolean;
  message: string;
}> {
  const response = await fetch(
    `${this.baseUrl}/resend-verification`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
      }),
    }
  );

  /*
   * On lit d'abord en texte pour ne pas planter
   * si le backend renvoie un body vide.
   */
  const rawBody = await response.text();

  let result: {
    success?: boolean;
    message?: string;
    code?: string;
  } = {};

  if (rawBody) {
    try {
      result = JSON.parse(rawBody);
    } catch {
      console.error(
        'Invalid resend-verification response:',
        rawBody
      );
    }
  }

  if (!response.ok) {
    throw new Error(
      result.message ||
        `Erreur serveur lors du renvoi de l'email (${response.status})`
    );
  }

  return {
    success: result.success ?? true,
    message:
      result.message ||
      "Un nouvel email de vérification a été envoyé.",
  };
}
  /**
   * Login client
   */
 async login(
  data: ClientLoginRequest
): Promise<ClientLoginResponse> {
  const response = await fetch(
    `${this.baseUrl}/login`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  const result = await response.json();

  if (!response.ok) {
    const err = new Error(
      result.message || 'Login failed'
    ) as Error & {
      code?: string;
      email?: string;
      status?: number;
    };

    err.code = result.code;
    err.email = result.email;
    err.status = response.status;

    throw err;
  }

  return result;
}

  /**
   * Logout client
   */
  async logout(refreshToken: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || 'Logout failed');
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(data: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const response = await fetch(`${this.baseUrl}/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Token refresh failed');
    }

    return result;
  }

  /**
   * Verify email address
   */
  async verifyEmail(data: EmailVerificationRequest): Promise<void> {
    const response = await fetch(`${this.baseUrl}/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Email verification failed');
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(data: PasswordResetRequest): Promise<PasswordResetResponse> {
    const response = await fetch(`${this.baseUrl}/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Password reset request failed');
    }

    return result;
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: PasswordResetConfirm): Promise<void> {
    const response = await fetch(`${this.baseUrl}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Password reset failed');
    }
  }

  /**
   * Change password for authenticated client
   */
  async changePassword(token: string, data: { currentPassword: string; newPassword: string }): Promise<void> {
    const response = await fetch(`${this.baseUrl}/change-password`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Password change failed');
    }
  }

  /**
   * Update authenticated client's profile
   */
  async updateProfile(
    token: string,
    data: {
      firstName: string;
      lastName: string;
      phone: string;
      dateOfBirth?: string;
      isMinor?: boolean;
      legalGuardian?: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        relationshipToMinor: 'parent' | 'legal_guardian' | 'other';
        relationshipDetails?: string;
      } | null;
    }
  ): Promise<{ success: boolean; user: ClientUser }> {
    const response = await fetch(`${this.baseUrl}/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Profile update failed');
    }

    return result;
  }

  /**
   * Delete authenticated client's account (password confirmation required)
   */
  async deleteAccount(
    token: string,
    data: { password: string; reason?: string }
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseUrl}/account`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      const err = new Error(result.message || 'Account deletion failed') as Error & {
        code?: string;
        status?: number;
      };

      err.code = result.code;
      err.status = response.status;

      throw err;
    }

    return result;
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(token: string): Promise<{ user: ClientUser }> {
    const response = await fetch(`${this.baseUrl}/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to get user info');
    }

    return result;
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  }

  /**
   * Extract token expiry time
   */
  getTokenExpiry(token: string): Date | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  }
}

// Export singleton instance
export const clientAuthApi = new ClientAuthApi();
export default clientAuthApi;
